/**
 * ═══════════════════════════════════════════════════════════════
 * FREEZE DEBUGGER — Tracks UI freezes, click responsiveness,
 * API call states, event loop blocks, and memory leaks.
 * 
 * Remove this file once the freeze issue is resolved.
 * ═══════════════════════════════════════════════════════════════
 */

const FREEZE_DEBUG = true
const FREEZE_THRESHOLD_MS = 2000 // Alert if event loop is blocked > 2s

// ── 1. Event Loop Freeze Detector ──
// Checks every 500ms — if the gap between ticks exceeds threshold, we're frozen
let lastTick = Date.now()
let freezeCount = 0
const freezeChecker = setInterval(() => {
  const now = Date.now()
  const gap = now - lastTick
  if (gap > FREEZE_THRESHOLD_MS) {
    freezeCount++
    console.error(
      `%c🧊 FREEZE DETECTED #${freezeCount}! Event loop was blocked for ${gap}ms (threshold: ${FREEZE_THRESHOLD_MS}ms)`,
      'color: #fff; background: #dc2626; padding: 4px 8px; font-size: 14px; font-weight: bold; border-radius: 4px'
    )
    console.error(`🧊 Freeze ended at: ${new Date().toLocaleTimeString()}`)
    
    // Check what might be causing it
    console.group('🧊 Freeze Debug Info')
    console.log('Active timers/intervals: Check via Performance tab')
    console.log('Pending API calls:', window.__pendingAPICalls || 'tracking not active')
    console.log('Last click target:', window.__lastClickTarget || 'none')
    console.log('Last click time:', window.__lastClickTime ? new Date(window.__lastClickTime).toLocaleTimeString() : 'none')
    console.log('Memory:', performance.memory ? `${Math.round(performance.memory.usedJSHeapSize / 1048576)}MB / ${Math.round(performance.memory.jsHeapSizeLimit / 1048576)}MB` : 'N/A')
    console.groupEnd()
  }
  lastTick = now
}, 500)


// ── 2. Click Logger — tracks every click and response time ──
let clickCount = 0
document.addEventListener('click', (e) => {
  clickCount++
  const target = e.target
  const tagName = target.tagName
  const id = target.id ? `#${target.id}` : ''
  const classes = target.className && typeof target.className === 'string' ? `.${target.className.split(' ').join('.')}` : ''
  const text = (target.textContent || '').trim().substring(0, 40)
  const selector = `${tagName}${id}${classes}`
  
  window.__lastClickTarget = selector
  window.__lastClickTime = Date.now()
  
  console.log(
    `%c👆 CLICK #${clickCount}: ${selector} → "${text}"`,
    'color: #0ea5e9; font-weight: bold'
  )

  // Track if the click handler is slow
  const clickStart = performance.now()
  requestAnimationFrame(() => {
    const elapsed = performance.now() - clickStart
    if (elapsed > 100) {
      console.warn(`%c⚠️ SLOW CLICK RESPONSE: ${elapsed.toFixed(0)}ms for "${text}" (${selector})`, 'color: #f59e0b; font-weight: bold')
    }
  })
}, true) // Use capture phase to catch ALL clicks


// ── 3. Pending API Call Tracker ──
window.__pendingAPICalls = new Map()
let apiCallId = 0

// Monkey-patch XMLHttpRequest to track all AJAX calls
const origOpen = XMLHttpRequest.prototype.open
const origSend = XMLHttpRequest.prototype.send
XMLHttpRequest.prototype.open = function(method, url) {
  this.__debugId = ++apiCallId
  this.__debugMethod = method
  this.__debugUrl = url
  this.__debugStartTime = null
  return origOpen.apply(this, arguments)
}
XMLHttpRequest.prototype.send = function() {
  const id = this.__debugId
  const method = this.__debugMethod
  const url = this.__debugUrl
  this.__debugStartTime = Date.now()
  
  window.__pendingAPICalls.set(id, { method, url, started: new Date().toLocaleTimeString() })
  
  this.addEventListener('loadend', () => {
    window.__pendingAPICalls.delete(id)
    const elapsed = Date.now() - this.__debugStartTime
    if (elapsed > 5000) {
      console.warn(`%c🐌 VERY SLOW API: ${method} ${url} took ${elapsed}ms`, 'color: #ef4444; font-weight: bold')
    }
  })
  
  // Timeout warning
  setTimeout(() => {
    if (window.__pendingAPICalls.has(id)) {
      console.error(
        `%c🚨 API STUCK for 30s+: ${method} ${url} — This may be causing the freeze!`,
        'color: #fff; background: #ef4444; padding: 2px 6px; font-weight: bold'
      )
    }
  }, 30000)
  
  return origSend.apply(this, arguments)
}

// Monkey-patch fetch too
const origFetch = window.fetch
window.fetch = function(url, options) {
  const id = ++apiCallId
  const method = (options?.method || 'GET').toUpperCase()
  const urlStr = typeof url === 'string' ? url : url.url || 'unknown'
  
  window.__pendingAPICalls.set(id, { method, url: urlStr, started: new Date().toLocaleTimeString() })
  
  return origFetch.apply(this, arguments)
    .then(response => {
      window.__pendingAPICalls.delete(id)
      return response
    })
    .catch(err => {
      window.__pendingAPICalls.delete(id)
      throw err
    })
}


// ── 4. Unhandled Promise Rejection Catcher ──
window.addEventListener('unhandledrejection', (event) => {
  console.error(
    `%c💥 UNHANDLED PROMISE REJECTION — This can cause UI freeze!`,
    'color: #fff; background: #dc2626; padding: 2px 6px; font-weight: bold'
  )
  console.error('Rejection reason:', event.reason)
  console.error('Promise:', event.promise)
})


// ── 5. Periodic Status Report ──
setInterval(() => {
  const pending = window.__pendingAPICalls.size
  if (pending > 0) {
    console.log(
      `%c📊 STATUS: ${pending} pending API call(s), ${clickCount} clicks, ${freezeCount} freezes`,
      'color: #6366f1; font-weight: bold'
    )
    window.__pendingAPICalls.forEach((val, key) => {
      console.log(`   ⏳ [${key}] ${val.method} ${val.url} (started: ${val.started})`)
    })
  }
}, 10000) // Every 10 seconds


// ── 6. Visibility Change Logger ──
document.addEventListener('visibilitychange', () => {
  const state = document.visibilityState
  console.log(
    `%c👁️ TAB ${state === 'visible' ? 'VISIBLE' : 'HIDDEN'} at ${new Date().toLocaleTimeString()}`,
    `color: ${state === 'visible' ? '#22c55e' : '#6c757d'}; font-weight: bold`
  )
  if (state === 'visible') {
    const pending = window.__pendingAPICalls.size
    if (pending > 0) {
      console.warn(`%c⚠️ TAB RESUMED with ${pending} pending API calls — these may be stale!`, 'color: #f59e0b; font-weight: bold')
      window.__pendingAPICalls.forEach((val, key) => {
        console.warn(`   🔴 STALE? [${key}] ${val.method} ${val.url} (started: ${val.started})`)
      })
    }
  }
})


// ── 7. Memory Leak Detector ──
if (performance.memory) {
  let lastHeap = performance.memory.usedJSHeapSize
  setInterval(() => {
    const currentHeap = performance.memory.usedJSHeapSize
    const diff = currentHeap - lastHeap
    const diffMB = (diff / 1048576).toFixed(1)
    const totalMB = (currentHeap / 1048576).toFixed(1)
    
    if (diff > 5 * 1048576) { // More than 5MB growth
      console.warn(
        `%c🧠 MEMORY GROWTH: +${diffMB}MB (now: ${totalMB}MB) — Possible memory leak!`,
        'color: #f59e0b; font-weight: bold'
      )
    }
    lastHeap = currentHeap
  }, 30000) // Every 30 seconds
}


// ── Startup Banner ──
console.log(
  `%c🔍 FREEZE DEBUGGER ACTIVE — Monitoring clicks, API calls, event loop, memory`,
  'color: #fff; background: #6366f1; padding: 6px 12px; font-size: 13px; font-weight: bold; border-radius: 4px'
)
console.log(
  `%c   Threshold: ${FREEZE_THRESHOLD_MS}ms | To see pending API calls: window.__pendingAPICalls`,
  'color: #6366f1'
)

export default { FREEZE_DEBUG }
